import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, ArrowRight } from 'lucide-react';

const portfolioItems = [
  {
    id: 1,
    title: 'Tech Startup SaaS',
    category: 'Business Website',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop',
    tags: ['React', 'Node.js', 'AWS'],
  },
  {
    id: 2,
    title: 'Fashion E-Commerce',
    category: 'Custom Website',
    image: 'https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=800&h=500&fit=crop',
    tags: ['Next.js', 'Stripe', 'MongoDB'],
  },
  {
    id: 3,
    title: 'Creative Portfolio',
    category: 'Personal Website',
    image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&h=500&fit=crop',
    tags: ['React', 'Framer Motion', 'Tailwind'],
  },
  {
    id: 4,
    title: 'Product Launch',
    category: 'Landing Page',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop',
    tags: ['Next.js', 'Vercel', 'Analytics'],
  },
  {
    id: 5,
    title: 'Healthcare Platform',
    category: 'Custom Website',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=500&fit=crop',
    tags: ['React', 'Node.js', 'PostgreSQL'],
  },
  {
    id: 6,
    title: 'Real Estate Agency',
    category: 'Business Website',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=500&fit=crop',
    tags: ['Next.js', 'Prisma', 'Maps API'],
  },
];

const Portfolio = () => {
  return (
    <section 
      id="portfolio" 
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #050508 0%, #06060c 50%, #050508 100%)',
      }}
      data-testid="portfolio-section"
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#7B2EFF]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#00D4FF]/5 rounded-full blur-[120px]" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] text-[#7B2EFF] bg-[#7B2EFF]/10 border border-[#7B2EFF]/20 backdrop-blur-sm mb-4">
            Our Work
          </span>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold text-white">Featured Projects</h2>
          <p className="mt-4 text-white/50 max-w-2xl mx-auto text-lg">
            A selection of our finest work. Each project represents our commitment to excellence.
          </p>
        </motion.div>

        {/* Portfolio Grid - Glass Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolioItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative rounded-3xl overflow-hidden glass-card"
              data-testid={`portfolio-item-${item.id}`}
            >
              {/* Image */}
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a12] via-[#0a0a12]/50 to-transparent" />
              </div>

              {/* Content Overlay */}
              <div className="absolute inset-0 p-5 flex flex-col justify-end">
                {/* Category pill */}
                <span className="inline-block w-fit px-3 py-1 rounded-full text-xs text-[#7B2EFF] bg-[#7B2EFF]/10 border border-[#7B2EFF]/20 backdrop-blur-sm mb-2">
                  {item.category}
                </span>
                
                {/* Title */}
                <h3 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                  {item.title}
                  <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {item.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 rounded-lg text-xs text-white/60 bg-white/5 border border-white/10 backdrop-blur-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Hover glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none rounded-3xl"
                style={{
                  boxShadow: 'inset 0 0 60px rgba(123, 46, 255, 0.15)',
                }}
              />
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <motion.button 
            className="px-8 py-4 rounded-xl font-semibold glass-card inline-flex items-center gap-2 group text-white"
            whileHover={{ scale: 1.02 }}
          >
            View All Projects
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;
